import { Component, OnInit, OnDestroy, Directive, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { Router } from '@angular/router';

import { AppConstants } from 'appshared/app-constants';
import * as Models from 'appshared/shared-models';
import { TextTools } from 'appshared/text-tools';
import { MiscTools } from 'appshared/misc-tools';
import { ValidationTools } from 'appshared/validation-tools';

import { AuthService } from 'client/app/services/auth.service';
import { UiAlertsService } from 'client/app/components/ui-alerts/ui-alerts.service';

import { PagingSearchBarComponent } from 'client/app/components/shared/paging-search-bar/paging-search-bar.component';

@Component({
	selector: 'app-table-organizations',
	templateUrl: './table-organizations.component.html',
	styleUrls: ['./table-organizations.component.scss']
})
export class TableOrganizationsComponent implements OnInit {
	ac = AppConstants;
	tt = TextTools;
	mt = MiscTools;

	now = new Date();

	@ViewChild(PagingSearchBarComponent) pagingSearchBar: PagingSearchBarComponent = null;

	/*
		pass in
			users 
			storageKey
			options
			- showOrganization : true
	*/

	authUser: Models.AuthUser = null;
	loading: boolean = false;
	starIcon = ['fas', 'star'];

	displayOptions: Models.TableDisplayOptions = new Models.TableDisplayOptions();
	pagingOptions: Models.TablePagingOptions = new Models.TablePagingOptions();
	storageKey: string = '';

	showFavorite: boolean = false;
	showSalesEng: boolean = true;
	showSalesRep: boolean = true;
	userId: number = 0;

	items: Models.Organization[] = [];
	itemsToShow: Models.Organization[] = [];

	columnDefs: Models.ColumnDefinition[] = [
		{
			field: 'is_favorite',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Favorite',
			icon: this.starIcon
		}, {
			field: 'is_enabled',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Enabled/Disabled',
			icon: 'power-off'
		}, {
			field: '__issues',
			type: 'baseText',
			sortType: 'text',
			label: null,
			toolTip: 'Issues',
			icon: 'exclamation-triangle'
		}, {
			field: 'name',
			type: 'baseText',
			sortType: 'text',
			label: 'Name',
			toolTip: null,
			icon: null
		}, {
			field: 'otype',
			type: 'baseText',
			sortType: 'text',
			label: 'Type',
			toolTip: null,
			icon: null
		}, {
			field: '__roles',
			type: 'baseText',
			sortType: 'text',
			label: 'Your Role(s)',
			toolTip: null,
			icon: null
		}, {
			field: 'salesforce_account_owner',
			type: 'baseText',
			sortType: 'text',
			label: null,
			toolTip: 'Account Owner',
			icon: ['fab', 'salesforce']
		}, {
			field: 'salesforce_se',
			type: 'baseText',
			sortType: 'text',
			label: null,
			toolTip: 'SE/Sales Engineer',
			icon: ['fas', 'user-graduate']
		}, {
			field: 'salesforce_tam',
			type: 'baseText',
			sortType: 'text',
			label: null,
			toolTip: 'TAM/Technical Account Manager',
			icon: ['fas', 'user-ninja']
		}, {
			field: 'salesforce_sa',
			type: 'baseText',
			sortType: 'text',
			label: null,
			toolTip: 'SA/Solutions Architect',
			icon: ['fas', 'user-astronaut']
		}, {
			field: 'num_users',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Number of Users',
			icon: AppConstants.sectionIcons.users
		}, {
			field: '__num_groups',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Number of Groups',
			icon: AppConstants.sectionIcons.organizationgroups
		}, {
			field: '__num_products_platforms',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Number of Product/Platforms',
			icon: AppConstants.sectionIcons.platforms
		}, {
			field: '__num_builds',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Number of Direct Access Builds',
			icon: AppConstants.sectionIcons.builds
		}, {
			field: '__num_documents',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Number of Direct Access Documents',
			icon: AppConstants.sectionIcons.documents
		}, {
			field: 'num_partnerships',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Number of Partnerships',
			icon: AppConstants.sectionIcons.partneradmin
		}, {
			field: 'num_active_zen_domains',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Number of Active ZEN Master Sites',
			icon: AppConstants.sectionIcons.zencustomers
		}, {
			field: 'num_keys',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Number of Linked Keys',
			icon: AppConstants.sectionIcons.licensing
		}, {
			field: 'num_billing_codes',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Number of Billing Codes',
			icon: ['fas', 'sack-dollar']
		}, {
			field: 'added_on',
			type: 'baseDate',
			sortType: 'numeric',
			label: null,
			toolTip: 'Days Since Added',
			icon: 'clock'
		}, {
			field: 'last_download',
			type: 'baseDate',
			sortType: 'numeric',
			label: null,
			toolTip: 'Days Since Last Download',
			icon: 'file-download'
		}
	];

	fieldsToShow: string[] = [];
	allFieldsToShow: string[] = [];
	lockedFields: string[] = [];
	baselineFields: string[] = [];
	minimalFields: string[] = [];


	// ------------------------------------------------------------------------
	constructor(
		private router: Router,
		private uiAlertsService: UiAlertsService,
		private authService: AuthService) {
	}

	// ------------------------------------------------------------------------
	ngOnInit(): void {
		const sessionUser: Models.AuthUser = this.authService.getUser();
		if (sessionUser != null && sessionUser.id !== 0) {
			this.authUser = sessionUser;
			const starPref = TextTools.getUserPropValue(this.authUser, AppConstants.starIconKey);
			if (starPref && starPref !== '') this.starIcon = ['fas', starPref];

			this.setups();
		} // if
	}

	// ------------------------------------------------------------------------
	async setups() {

	}

	// ------------------------------------------------------------------------
	updateContent(items: Models.Organization[], storageKey: string, options: any = {}) {
		this.loading = true;

		this.items = items;
		this.storageKey = storageKey;

		let optionKeys: string[] = [];
		if (options) optionKeys = Object.keys(options);

		if (optionKeys.includes('showshowFavoriteSalesEng')) this.showFavorite = options.showFavorite;
		if (optionKeys.includes('showSalesEng')) this.showSalesEng = options.showSalesEng;
		if (optionKeys.includes('showSalesRep')) this.showSalesRep = options.showSalesRep;
		if (optionKeys.includes('userId')) this.userId = options.userId;

		this.fieldsToShow = [];
		this.allFieldsToShow = [];
		this.lockedFields = [];
		this.baselineFields = [];
		this.minimalFields = [];

		if (this.showFavorite) this.allFieldsToShow.push('is_favorite');
		this.allFieldsToShow.push('is_enabled');
		this.allFieldsToShow.push('__issues');
		this.allFieldsToShow.push('name');
		this.lockedFields.push('name');
		this.allFieldsToShow.push('otype');
		if (this.userId) this.allFieldsToShow.push('__roles');
		if (this.showSalesRep) this.allFieldsToShow.push('salesforce_account_owner');
		if (this.showSalesEng) this.allFieldsToShow.push('salesforce_se');
		if (this.showSalesEng) this.allFieldsToShow.push('salesforce_tam');
		if (this.showSalesEng) this.allFieldsToShow.push('salesforce_sa');
		this.allFieldsToShow.push('num_users');
		this.allFieldsToShow.push('__num_groups');
		this.allFieldsToShow.push('__num_products_platforms');
		this.allFieldsToShow.push('__num_builds');
		this.allFieldsToShow.push('__num_documents');
		this.allFieldsToShow.push('num_partnerships');
		this.allFieldsToShow.push('num_active_zen_domains');
		this.allFieldsToShow.push('num_keys');
		this.allFieldsToShow.push('num_billing_codes');
		this.allFieldsToShow.push('added_on');
		this.allFieldsToShow.push('last_download');

		this.baselineFields = MiscTools.deepClone(this.allFieldsToShow);


		for (const item of this.items) {
			item['__issues'] = ValidationTools.getOrganizationIssues(item, true).join(' ');

			if (item.salesforce_account_owner && item.salesforce_account_owner !== 'Unknown'
				&& item.salesforce_account_owner.length > 2)
				item['__short_owner'] = TextTools.acronym(item.salesforce_account_owner);

			if (item.salesforce_se && item.salesforce_se !== 'Unknown'
				&& item.salesforce_se.length > 2)
				item['__short_se'] = TextTools.acronym(item.salesforce_se);

			if (item.salesforce_tam && item.salesforce_tam !== 'Unknown'
				&& item.salesforce_tam.length > 2)
				item['__short_tam'] = TextTools.acronym(item.salesforce_tam);

			if (item.salesforce_sa && item.salesforce_sa !== 'Unknown'
				&& item.salesforce_sa.length > 2)
				item['__short_sa'] = TextTools.acronym(item.salesforce_sa);

			item['__num_groups'] = item.group_ids.length;
			item['__num_products_platforms'] = item.product_platforms.length;
			item['__num_builds'] = item.build_ids.length;
			item['__num_documents'] = item.document_ids.length;

			const roles: string[] = [];
			if (this.userId && item.salesforce_account_owner_id === this.userId) roles.push('Account Owner');
			if (this.userId && item.salesforce_se_id === this.userId) roles.push('Sales Engineer');
			if (this.userId && item.salesforce_tam_id === this.userId) roles.push('Technical Account Manager');
			if (this.userId && item.salesforce_sa_id === this.userId) roles.push('Solution Architect');
			item['__roles'] = roles.join(', ');
		} // for

		this.pagingSearchBar.setupPaging(this.items, this.columnDefs, this.storageKey, 'name', 'asc', false, this.allFieldsToShow, this.lockedFields, this.baselineFields, this.minimalFields);

		this.loading = false;
	} // updateContent

	// ------------------------------------------------------------------------
	filterItems(matchWords: string[]): any[] {

		const tmpList: Models.Organization[] = [];
		for (const item of this.items) {
			let match: boolean = true;
			if (matchWords.length > 0) {
				let matches: number = 0;
				for (const w of matchWords) {
					if (item.name.toLowerCase().includes(w)
						|| item.otype.toLowerCase().includes(w)
						|| (this.showSalesRep && item.salesforce_account_owner && item.salesforce_account_owner.toLowerCase().includes(w))
						|| (this.showSalesEng && item.salesforce_se && item.salesforce_se.toLowerCase().includes(w))
						|| (this.showSalesEng && item.salesforce_sa && item.salesforce_sa.toLowerCase().includes(w))
						|| (this.showSalesEng && item.salesforce_tam && item.salesforce_tam.toLowerCase().includes(w))
						|| (item['__issues'] && item['__issues'].toLowerCase().includes(w))
						|| (item['__roles'] && item['__roles'].toLowerCase().includes(w))
					)
						matches++;
				} // for
				if (matches !== matchWords.length) match = false;
			} // if

			if (match) tmpList.push(item);
		} // for

		return tmpList;
	} // filterItems

	// ------------------------------------------------------------------------
	setSortBy(field: string) {
		this.pagingSearchBar.setSortBy(field);
	} // setSortBy

	// ------------------------------------------------------------------------
	// Catch calls from the paging/search bar
	// ------------------------------------------------------------------------
	getParentMethod(): any {
		return {
			filterItems: (matchWords: string[]) => {
				return this.filterItems(matchWords);
			},
			updateItemsToShow: (itemsToShow: any[], displayOptions: Models.TableDisplayOptions, pagingOptions: Models.TablePagingOptions) => {
				this.itemsToShow = itemsToShow;
				this.displayOptions = displayOptions;
				this.pagingOptions = pagingOptions;
			},
			updateFieldsToShow: (fieldsToShow: string[]) => {
				this.fieldsToShow = fieldsToShow;
			},
			setPageNum: (pageNum: number, uniqueId: string) => {
				if (this.pagingSearchBar) this.pagingSearchBar.setPageNum(pageNum);
			},
			setSortBy: (field: string) => {
				this.setSortBy(field);
			}
		}
	}

}
